/**
 * Trim transparent padding from a logo image.
 *
 * Many logo files (AI-generated ones especially, and anything run through background removal)
 * are a large square canvas with the actual mark floating in the middle surrounded by transparent
 * pixels. When such a logo is sized by height — in the Brand Manager preview AND on the live site
 * header — the empty padding inflates the box while the visible mark stays tiny. Cropping to the
 * mark's real bounding box (plus a small breathing margin) makes the logo fill the space it's given.
 *
 * Operates on a local Blob (upload File / background-removal output / data-URI) so there's no
 * cross-origin canvas taint. Fully-opaque images (no transparent pixels) and images that are already
 * tight are returned unchanged — trimming only ever removes empty space, never part of the mark.
 * Any failure (decode error, tainted canvas, unexpected shape) falls back to the original input.
 */
export async function trimTransparentPadding(
  input: Blob,
  opts: { alphaThreshold?: number; marginRatio?: number } = {},
): Promise<Blob> {
  const alpha = opts.alphaThreshold ?? 12;
  const marginRatio = opts.marginRatio ?? 0.05;
  let url: string | null = null;
  try {
    url = URL.createObjectURL(input);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('decode failed'));
      img.src = url!;
    });
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return input;

    // Measure the alpha bounding box on a downscaled copy (fast — caps the pixel scan regardless of
    // source size), then map the bounds back to full resolution to crop losslessly.
    const scale = Math.min(1, 400 / Math.max(w, h));
    const mw = Math.max(1, Math.round(w * scale));
    const mh = Math.max(1, Math.round(h * scale));
    const probe = document.createElement('canvas');
    probe.width = mw;
    probe.height = mh;
    const pctx = probe.getContext('2d');
    if (!pctx) return input;
    pctx.drawImage(img, 0, 0, mw, mh);
    let data: Uint8ClampedArray;
    try {
      data = pctx.getImageData(0, 0, mw, mh).data;
    } catch {
      return input; // tainted canvas — shouldn't happen for local blobs, but bail safely
    }

    let minX = mw, minY = mh, maxX = -1, maxY = -1;
    for (let y = 0; y < mh; y++) {
      for (let x = 0; x < mw; x++) {
        if (data[(y * mw + x) * 4 + 3] > alpha) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX || maxY < minY) return input; // fully transparent — leave as-is

    // Map probe-space bounds back to source pixels.
    let sx0 = Math.floor(minX / scale);
    let sy0 = Math.floor(minY / scale);
    let sx1 = Math.min(w, Math.ceil((maxX + 1) / scale));
    let sy1 = Math.min(h, Math.ceil((maxY + 1) / scale));
    let cw = sx1 - sx0;
    let ch = sy1 - sy0;
    if (cw <= 0 || ch <= 0) return input;

    // Already tight (opaque logos, or content that fills the canvas) → don't re-encode needlessly.
    if (cw >= w * 0.97 && ch >= h * 0.97) return input;

    // Add a small even margin so the mark isn't flush against the edge.
    const margin = Math.round(Math.max(cw, ch) * marginRatio);
    sx0 = Math.max(0, sx0 - margin);
    sy0 = Math.max(0, sy0 - margin);
    cw = Math.min(w - sx0, cw + margin * 2);
    ch = Math.min(h - sy0, ch + margin * 2);

    const out = document.createElement('canvas');
    out.width = cw;
    out.height = ch;
    const octx = out.getContext('2d');
    if (!octx) return input;
    octx.drawImage(img, sx0, sy0, cw, ch, 0, 0, cw, ch);

    const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
    return blob ?? input;
  } catch {
    return input;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}
